import { useEffect, useState } from 'react';
import type { Operaciones } from '../../../api/operaciones';
import type { Hueco, NuevaReserva as Peticion, PeticionReserva, Presupuesto, ResultadoHuecos } from '../../../api/ops/reservasGestion';
import { claveIdempotencia } from '../../../api/operaciones';
import { ErrorApi } from '../../../api/problema';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Campo } from '../../../componentes/Campo';
import { Desplegable } from '../../../componentes/Desplegable';
import { Fecha } from '../../../componentes/Fecha';
import { Icono } from '../../../componentes/Icono';
import { Interruptor } from '../../../componentes/Interruptor';
import { OpcionRadio } from '../../../componentes/OpcionRadio';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { Segmentos } from '../../../componentes/Segmentos';
import { dinero } from '../../../logica/dinero';
import { avisoBreveDeError } from '../../../logica/estados';
import { usePermisos } from '../../../permisos/permisos';
import { Contador } from '../../../componentes/Contador';
import type { ContextoReservas } from '../contexto';
import { diaLargo, horaEn } from '../fechas';
import { mayuscula } from '../textos';
import { ERRORES_CREAR, formaDeCobro, porQueSoloEnlace } from './cobro';
import '../../sala/sala.css';

/**
 * 8.2 Nueva reserva (RV Panel «formulario»): qué y cuándo (zona, recurso opcional, día y personas), las horas libres del
 * mismo motor que la web (`searchReservationSlots`, G38), «Reservar igualmente» con motivo para quien puede forzar (plazo o
 * capacidad; el horario no se fuerza, G257), el evento si hay varios (G18, G21), el cliente (buscar o sus datos, G42), lo
 * que cobra la política (`quoteReservation`) y cómo se cobra: enlace o «Ya lo ha pagado aquí» con Efectivo o Datáfono
 * (G461), que no se ofrece con garantía ni con señal y garantía (G469, G793). Notas sin alergias (G192).
 */
interface Props {
    abierto: boolean;
    movil: boolean;
    ops: Operaciones;
    negocio: string;
    contexto: ContextoReservas;
    diaInicial: string;
    onCerrar: () => void;
    onHecha: (reservaId: string, dia: string) => void;
}

type ClienteElegido = { tipo: 'ficha'; id: string; nombre: string; detalle: string } | { tipo: 'nuevo' };

const PERMISOS = { plazo: 'reservas.reservas.forzar_plazo', capacidad: 'reservas.reservas.forzar_capacidad', clientes: 'clientes.fichas.ver' } as const;

export function NuevaReserva({ abierto, movil, ops, negocio, contexto, diaInicial, onCerrar, onHecha }: Props) {
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const zonas = contexto.zonas.filter((z) => z.status === 'active' && z.modality !== 'stay');
    const [zonaElegida, setZona] = useState('');
    // Las zonas llegan después de abrir: sin elegir, la primera.
    const zona = zonas.some((z) => z.id === zonaElegida) ? zonaElegida : (zonas[0]?.id ?? '');
    const [recurso, setRecurso] = useState('');
    const [dia, setDia] = useState(diaInicial);
    const [personas, setPersonas] = useState(2);
    const [hueco, setHueco] = useState<Hueco | null>(null);
    const [busqueda, setBusqueda] = useState<{ clave: string; resultado: ResultadoHuecos | null; error: boolean } | null>(null);
    // «Reintentar» vuelve a buscar sin tocar lo escrito (8.2, estado de error).
    const [intento, setIntento] = useState(0);
    const [forzar, setForzar] = useState(false);
    const [tipoForzar, setTipoForzar] = useState<'window' | 'capacity'>(permisos.puede(PERMISOS.plazo) ? 'window' : 'capacity');
    const [horaForzada, setHoraForzada] = useState('');
    const [motivo, setMotivo] = useState('');
    const [evento, setEvento] = useState('');
    const [presupuesto, setPresupuesto] = useState<{ clave: string; datos: Presupuesto | null } | null>(null);
    const [texto, setTexto] = useState('');
    const [encontrados, setEncontrados] = useState<{ q: string; lista: { id: string; nombre: string; detalle: string }[] } | null>(null);
    const [cliente, setCliente] = useState<ClienteElegido | null>(null);
    const [nuevo, setNuevo] = useState({ nombre: '', telefono: '', email: '' });
    const [cobro, setCobro] = useState<'link' | 'external_paid'>('link');
    const [metodo, setMetodo] = useState<'cash' | 'card_terminal'>('cash');
    const [notaCliente, setNotaCliente] = useState('');
    const [notaInterna, setNotaInterna] = useState('');
    const [ocupado, setOcupado] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [clave] = useState(() => claveIdempotencia());
    const puedeForzar = permisos.puede(PERMISOS.plazo) || permisos.puede(PERMISOS.capacidad);
    const zonaActual = zonas.find((z) => z.id === zona);
    const recursosZona = contexto.recursos.filter((r) => r.status === 'active' && r.current_terms.zone_id === zona);
    const destino: PeticionReserva['target'] = recurso === '' ? { zone_id: zona } : { resource_id: recurso };
    const claveBusqueda = JSON.stringify([zona, recurso, dia, personas, intento]);

    // Horas libres (el mismo motor que la web).
    useEffect(() => {
        if (!abierto || zona === '') {
            return;
        }
        let vivo = true;
        ops.searchReservationSlots(negocio, { target: recurso === '' ? { zone_id: zona } : { resource_id: recurso }, party_size: personas, from_date: dia, to_date: dia, mode: 'list' })
            .then((resultado) => { if (vivo) { setBusqueda({ clave: claveBusqueda, resultado, error: false }); } })
            .catch(() => { if (vivo) { setBusqueda({ clave: claveBusqueda, resultado: null, error: true }); } });
        return () => { vivo = false; };
    }, [abierto, ops, negocio, zona, recurso, dia, personas, claveBusqueda]);

    const huecos = busqueda?.clave === claveBusqueda ? (busqueda.resultado?.slots ?? []) : null;
    const horaElegida = forzar ? (/^\d{2}:\d{2}$/.test(horaForzada) ? horaForzada : null) : hueco !== null && huecos?.includes(hueco) === true ? (hueco.local_time ?? horaEn(hueco.starts_at, contexto.zona)) : null;
    const cuando: PeticionReserva['when'] | null = horaElegida === null ? null : !forzar && hueco?.shift_id != null ? { local_date: dia, shift_id: hueco.shift_id } : { local_date: dia, local_time: horaElegida };
    const peticion: PeticionReserva | null = cuando === null ? null : { target: destino, when: cuando, party_size: personas, ...(evento === '' ? {} : { event_id: evento }) };
    const clavePresupuesto = JSON.stringify(peticion);

    // Lo que cobra la política (Pagos), para la hora elegida.
    useEffect(() => {
        if (!abierto || peticion === null) {
            return;
        }
        let vivo = true;
        ops.quoteReservation(negocio, peticion)
            .then((datos) => { if (vivo) { setPresupuesto({ clave: clavePresupuesto, datos }); } })
            .catch(() => { if (vivo) { setPresupuesto({ clave: clavePresupuesto, datos: null }); } });
        return () => { vivo = false; };
        // `peticion` se rehace en cada pintado: su clave es la que cuenta.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [abierto, ops, negocio, clavePresupuesto]);

    // Buscar al cliente (nombre, teléfono o email).
    const q = texto.trim();
    useEffect(() => {
        if (q.length < 2 || !permisos.puede(PERMISOS.clientes)) {
            return;
        }
        let vivo = true;
        const t = setTimeout(() => {
            ops.listCustomers(negocio, { q, limit: 5 })
                .then((l) => {
                    if (vivo) {
                        setEncontrados({ q, lista: l.data.map((c) => ({ id: c.id, nombre: c.display_name, detalle: [c.phone, c.email].filter((x) => x != null && x !== '').join(' • ') })) });
                    }
                })
                .catch(() => { if (vivo) { setEncontrados({ q, lista: [] }); } });
        }, 250);
        return () => {
            vivo = false;
            clearTimeout(t);
        };
    }, [ops, negocio, q, permisos]);

    const quote = presupuesto?.clave === clavePresupuesto ? presupuesto.datos : null;
    const forma = formaDeCobro(quote);
    const cobroReal = forma === 'enlace-o-aqui' ? cobro : forma === 'solo-enlace' ? 'link' : 'none';
    const eventos = quote?.event_choices ?? [];
    const clienteListo = cliente?.tipo === 'ficha' || (cliente?.tipo === 'nuevo' && nuevo.nombre.trim() !== '' && (nuevo.telefono.trim() !== '' || nuevo.email.trim() !== ''));
    const listo = peticion !== null && quote !== null && clienteListo && (!forzar || motivo.trim() !== '') && (eventos.length < 2 || evento !== '');

    const reservar = () => {
        if (peticion === null || !listo) {
            return;
        }
        const datos: Peticion = {
            ...peticion,
            ...(cliente.tipo === 'ficha'
                ? { customer_id: cliente.id }
                : { contact: { display_name: nuevo.nombre.trim(), ...(nuevo.telefono.trim() === '' ? {} : { phone: nuevo.telefono.trim() }), ...(nuevo.email.trim() === '' ? {} : { email: nuevo.email.trim() }) } }),
            payment_collection: cobroReal,
            ...(cobroReal === 'external_paid' ? { external_payment_method: metodo } : {}),
            ...(forzar ? { override: { kind: tipoForzar, reason: motivo.trim() } } : {}),
            confirm_different_resource: false,
            ...(notaCliente.trim() === '' ? {} : { customer_note: notaCliente.trim() }),
            ...(notaInterna.trim() === '' ? {} : { staff_note: notaInterna.trim() }),
        };
        setOcupado(true);
        setError(null);
        ops.createReservation(negocio, datos, clave)
            .then((hecha) => {
                avisar({ tipo: 'ok', texto: hecha.next_action.kind === 'pay' ? 'Reserva hecha. Le hemos enviado el enlace de pago: tiene 30 minutos.' : cobroReal === 'external_paid' ? 'Reserva hecha. Se confirma en cuanto Pagos registre el cobro.' : 'Reserva hecha.' });
                onHecha(hecha.reservation.id, dia);
            })
            .catch((e: unknown) => {
                const codigo: string = e instanceof ErrorApi ? e.codigo : '';
                if (codigo in ERRORES_CREAR) {
                    setCobro('link');
                    setError(ERRORES_CREAR[codigo] ?? null);
                } else if (e instanceof ErrorApi && e.codigo === 'capacity_unavailable') {
                    setError('Ese hueco ya no está libre. Elige otra hora.');
                    setHueco(null);
                } else if (e instanceof ErrorApi && e.codigo === 'conflict') {
                    setError('El precio ha cambiado. Revisa lo que se cobra y vuelve a reservar.');
                    setPresupuesto(null);
                } else {
                    setError(avisoBreveDeError(e) ?? (e instanceof ErrorApi ? e.message : 'No se ha podido hacer la reserva.'));
                }
            })
            .finally(() => { setOcupado(false); });
    };

    const motivoSinHueco: Record<string, string> = {
        closed: 'Ese día está cerrado.',
        outside_window: 'Ese día está fuera del plazo para reservar.',
        no_capacity: 'No queda sitio ese día.',
        party_size_out_of_range: 'No se admiten grupos de ese tamaño en esta zona.',
    };

    return (
        <PanelLateral
            abierto={abierto}
            onCerrar={onCerrar}
            movil={movil}
            icono="calendar-plus"
            eyebrow="Reservas"
            titulo="Nueva reserva"
            subtitulo="Busca hueco, elige el cliente y cómo se cobra."
            accion="Reservar"
            accionIcono="calendar-check"
            ocupado={ocupado}
            ocupadoTexto="Reservando…"
            inactiva={!listo}
            onAccion={reservar}
        >
            <div className="rv-rs-nueva">
                <p className="rv-sl-ceja">QUÉ Y CUÁNDO</p>
                <div className="rv-rs-nueva__par">
                    <Desplegable etiqueta="Zona" valor={zona} opciones={zonas.map((z) => ({ valor: z.id, texto: z.name ?? '' }))} onChange={(v) => { setZona(v); setRecurso(''); setHueco(null); }} />
                    <Desplegable
                        etiqueta={`${mayuscula(contexto.palabra)} (opcional)`}
                        valor={recurso}
                        opciones={[{ valor: '', texto: zonaActual?.assignment_mode === 'on_floor' ? 'Se asigna en Sala' : 'La que asigne Reservaria' }, ...recursosZona.map((r) => ({ valor: r.id, texto: `${mayuscula(contexto.palabra)} ${r.code ?? ''} · ${String(r.current_terms.min_capacity)}–${String(r.current_terms.max_capacity)}` }))]}
                        onChange={(v) => { setRecurso(v); setHueco(null); }}
                    />
                </div>
                <div className="rv-rs-nueva__par">
                    <Fecha etiqueta="Día" valor={dia} onChange={(v) => { setDia(v); setHueco(null); }} />
                    <Contador etiqueta="Personas" ayuda="" valor={personas} onCambio={(n) => { setPersonas(n); setHueco(null); }} />
                </div>

                <p className="rv-sl-ceja">HORA</p>
                {busqueda?.clave === claveBusqueda && busqueda.error ? (
                    <div className="rv-rs-horas-error" role="alert">
                        <span>
                            <Icono nombre="wifi-off" />
                            <span><strong>No hemos podido buscar las horas libres.</strong> Lo que has puesto no se pierde: vuelve a intentarlo.</span>
                        </span>
                        <button type="button" className="rv-b rv-b-sec" onClick={() => { setIntento((i) => i + 1); }}><Icono nombre="rotate-cw" /><span>Reintentar</span></button>
                    </div>
                ) : huecos === null ? (
                    <div className="rv-rs-horas" role="status" aria-busy="true" aria-label="Buscando…">
                        {[1, 2, 3, 4, 5, 6].map((n) => <span key={n} className="rv-rs-hora rv-rs-hora--cargando" aria-hidden="true" />)}
                    </div>
                ) : huecos.length === 0 ? (
                    <p className="rv-sl-nota">{motivoSinHueco[busqueda?.resultado?.unavailable_reason ?? ''] ?? 'No hay horas libres ese día.'}</p>
                ) : (
                    <div className="rv-rs-horas" role="radiogroup" aria-label="Horas libres">
                        {huecos.map((h) => {
                            const t = h.local_time ?? horaEn(h.starts_at, contexto.zona);
                            const n = (h.events ?? []).length;
                            return (
                                <button key={`${h.starts_at}${h.shift_id ?? ''}`} type="button" role="radio" aria-checked={hueco === h && !forzar} disabled={forzar} className={`rv-rs-hora${hueco === h && !forzar ? ' rv-rs-hora--elegida' : ''}`} onClick={() => { setHueco(h); setEvento(''); }}>
                                    <strong>{t}</strong>
                                    {n > 0 && <small>{n === 1 ? 'Con evento' : `${String(n)} eventos`}</small>}
                                </button>
                            );
                        })}
                    </div>
                )}
                {puedeForzar && (
                    <Interruptor
                        etiqueta="Reservar igualmente"
                        icono="shield-check"
                        ayuda="Fuera de plazo o por encima de la capacidad. Queda registrado con el motivo. El horario no se fuerza."
                        valor={forzar}
                        onChange={setForzar}
                    />
                )}
                {forzar && (
                    <div className="rv-rs-nueva__forzar">
                        {permisos.puede(PERMISOS.plazo) && permisos.puede(PERMISOS.capacidad) && (
                            <Segmentos etiqueta="Qué se fuerza" items={[{ etiqueta: 'Fuera de plazo', icono: 'clock' }, { etiqueta: 'Sin sitio', icono: 'users' }]} valor={tipoForzar === 'window' ? 0 : 1} onCambio={(i) => { setTipoForzar(i === 0 ? 'window' : 'capacity'); }} />
                        )}
                        <Campo etiqueta="Hora (HH:MM)" valor={horaForzada} onChange={setHoraForzada} modo="numeric" max={5} />
                        <Campo etiqueta="Motivo" valor={motivo} onChange={setMotivo} max={500} />
                    </div>
                )}
                {eventos.length > 1 && (
                    <Desplegable etiqueta="Evento" valor={evento} opciones={[{ valor: '', texto: 'Elige el evento' }, ...eventos.map((e) => ({ valor: e.event_id, texto: `${e.public_name ?? 'Evento'} · ${horaEn(e.starts_at, contexto.zona)}` }))]} onChange={setEvento} ayuda="A esa hora hay más de un evento: elige a cuál va." />
                )}

                <p className="rv-sl-ceja">CLIENTE</p>
                {cliente?.tipo === 'ficha' ? (
                    <div className="rv-rs-cliente">
                        <span><strong>{cliente.nombre}</strong><small>{cliente.detalle}</small></span>
                        <button type="button" className="rv-b rv-b-sec" onClick={() => { setCliente(null); }}><Icono nombre="x" /><span>Cambiar</span></button>
                    </div>
                ) : cliente?.tipo === 'nuevo' ? (
                    <div className="rv-rs-nueva__forzar">
                        <Campo etiqueta="Nombre" valor={nuevo.nombre} onChange={(v) => { setNuevo((x) => ({ ...x, nombre: v })); }} />
                        <Campo etiqueta="Teléfono" tipo="tel" valor={nuevo.telefono} onChange={(v) => { setNuevo((x) => ({ ...x, telefono: v })); }} />
                        <Campo etiqueta="Email" tipo="email" valor={nuevo.email} onChange={(v) => { setNuevo((x) => ({ ...x, email: v })); }} ayuda="Teléfono o email: con uno basta para enviarle la reserva." />
                        <button type="button" className="rv-b rv-b-sec" onClick={() => { setCliente(null); }}><Icono nombre="search" /><span>Buscar un cliente</span></button>
                    </div>
                ) : (
                    <>
                        {permisos.puede(PERMISOS.clientes) && <Campo etiqueta="Nombre, teléfono o email" tipo="search" valor={texto} onChange={setTexto} />}
                        {encontrados !== null && encontrados.q === q && q.length >= 2 && (
                            <ul className="rv-rs-encontrados">
                                {encontrados.lista.length === 0 && <li className="rv-sl-nota">Nadie con esos datos.</li>}
                                {encontrados.lista.map((c) => (
                                    <li key={c.id}>
                                        <button type="button" onClick={() => { setCliente({ tipo: 'ficha', ...c }); }}>
                                            <span><strong>{c.nombre}</strong><small>{c.detalle}</small></span>
                                            <Icono nombre="plus" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <button type="button" className="rv-b rv-b-sec" onClick={() => { setCliente({ tipo: 'nuevo' }); }}><Icono nombre="user-plus" /><span>Dar de alta a alguien nuevo</span></button>
                    </>
                )}

                {quote !== null && (
                    <>
                        <p className="rv-sl-ceja">COBRO</p>
                        <dl className="rv-rs-presupuesto">
                            {quote.payment.policy_name != null && quote.payment.policy_name !== '' && <div><dt>Política de cobro</dt><dd>{quote.payment.policy_name}</dd></div>}
                            {quote.payment.terms_text !== undefined && quote.payment.terms_text !== '' && <dd className="rv-rs-presupuesto__texto">{quote.payment.terms_text}</dd>}
                            <div><dt>Total</dt><dd>{dinero(quote.total)}</dd></div>
                            {quote.free_cancellation_until != null && <div><dt>Cancelación gratis hasta</dt><dd>{`${mayuscula(diaLargo(quote.free_cancellation_until.slice(0, 10)))}, ${horaEn(quote.free_cancellation_until, contexto.zona)}`}</dd></div>}
                            {quote.payment.amount_now !== undefined && <div><dt>Se cobra ahora</dt><dd>{dinero(quote.payment.amount_now)}</dd></div>}
                        </dl>
                        {forma === 'enlace-o-aqui' && (
                            <>
                                <div className="rv-rs-nueva__par">
                                    <OpcionRadio elegida={cobro === 'link'} onElegir={() => { setCobro('link'); }} icono="send" titulo="Enviar enlace de pago" texto="Le llega por WhatsApp o email. Tiene 30 minutos; mientras, el hueco queda guardado." />
                                    <OpcionRadio elegida={cobro === 'external_paid'} onElegir={() => { setCobro('external_paid'); }} icono="banknote" titulo="Ya lo ha pagado aquí" texto="Efectivo o datáfono: queda como pago externo en Pagos." />
                                </div>
                                {cobro === 'external_paid' && (
                                    <Segmentos etiqueta="¿Cómo lo ha pagado?" items={[{ etiqueta: 'Efectivo', icono: 'banknote' }, { etiqueta: 'Datáfono', icono: 'credit-card' }]} valor={metodo === 'cash' ? 0 : 1} onCambio={(i) => { setMetodo(i === 0 ? 'cash' : 'card_terminal'); }} />
                                )}
                            </>
                        )}
                        {forma === 'solo-enlace' && <p className="rv-sl-nota">{`Le enviamos un enlace de pago. ${porQueSoloEnlace(quote)}`}</p>}
                        {forma === 'ninguno' && <p className="rv-sl-nota">Esta reserva no tiene cobro.</p>}
                    </>
                )}

                <p className="rv-sl-ceja">NOTAS</p>
                <Campo etiqueta="Lo que pide el cliente" valor={notaCliente} onChange={setNotaCliente} max={500} filas={2} ayuda="Sin alergias: esas las escribe el cliente al reservar." />
                <Campo etiqueta="Nota interna" valor={notaInterna} onChange={setNotaInterna} max={500} filas={2} />
                {error !== null && <p className="rv-sl-resultado rv-sl-resultado--mal" role="alert"><Icono nombre="triangle-alert" /><span>{error}</span></p>}
            </div>
        </PanelLateral>
    );
}
